import * as React from "react";
import {
    Box,
    Stack,
    Typography,
    Paper,
    TextField,
    IconButton,
    Button,
    Chip,
    CircularProgress,
    Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { adminFetch } from "../services/adminFetch";

type PropertyRow = {
    id: number;
    title: string;
    slug: string;
    status: string;
    city?: string | null;
    region?: string | null;
    price_per_night?: number | null;
    featured_image_url?: string | null;
};

export default function PropertiesPage() {
    const nav = useNavigate();

    const [items, setItems] = React.useState<PropertyRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState("");

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminFetch(
                `/api/admin/properties?q=${encodeURIComponent(q)}`
            );
            const data = await res.json();
            setItems(Array.isArray(data.items) ? data.items : []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [q]);

    React.useEffect(() => {
        load();
    }, [load]);

    return (
        <Stack spacing={2.5}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 1000 }}>
                        Properties
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your properties
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => nav("/admin/properties/new")}
                    sx={{ fontWeight: 900 }}
                >
                    Add property
                </Button>
            </Stack>

            {/* Search */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SearchIcon color="action" />
                    <TextField
                        placeholder="Search by title, city, slug…"
                        variant="standard"
                        fullWidth
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        InputProps={{ disableUnderline: true }}
                    />
                </Stack>
            </Paper>

            {/* List */}
            {loading ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                    <CircularProgress />
                </Box>
            ) : items.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
                    <Typography color="text.secondary">
                        No properties found.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={1.5}>
                    {items.map((p) => (
                        <Paper
                            key={p.id}
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                transition: "box-shadow 120ms ease",
                                "&:hover": {
                                    boxShadow: 4,
                                },
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={2}
                                alignItems={{ md: "center" }}
                            >
                                {/* Image */}
                                <Box
                                    sx={{
                                        width: { xs: "100%", md: 160 },
                                        height: 110,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: "action.hover",
                                        flexShrink: 0,
                                    }}
                                >
                                    {p.featured_image_url ? (
                                        <img
                                            src={p.featured_image_url}
                                            alt={p.title}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                            loading="lazy"
                                        />
                                    ) : null}
                                </Box>

                                {/* Info */}
                                <Box sx={{ flex: 1 }}>
                                    <Stack spacing={0.5}>
                                        <Typography sx={{ fontWeight: 900 }}>
                                            {p.title}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {p.city}
                                            {p.region ? `, ${p.region}` : ""}
                                        </Typography>

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip
                                                size="small"
                                                label={p.status}
                                                color={
                                                    p.status === "published"
                                                        ? "success"
                                                        : "default"
                                                }
                                            />
                                            {p.price_per_night ? (
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 800 }}
                                                >
                                                    €{p.price_per_night} / night
                                                </Typography>
                                            ) : null}
                                        </Stack>
                                    </Stack>
                                </Box>

                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{ display: { xs: "none", md: "block" } }}
                                />

                                {/* Actions */}
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        onClick={() =>
                                            nav(`/admin/properties/${p.id}`)
                                        }
                                        color="primary"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
